/**
 * 고르는 조각 — 라디오와 체크박스.
 *
 * **네이티브 `<input>` 을 숨겨서 쓴다.** `role="radio"` 를 손으로 붙이면
 * 화살표 이동·묶음 포커스·읽는 쪽 안내를 전부 다시 만들어야 하고, 그중
 * 하나만 빠뜨려도 키보드로는 못 고르는 화면이 된다. 네이티브를 `sr-only`
 * 로 감추고 겉만 꾸미면 그 전부가 공짜로 따라온다.
 *
 * **값은 글자로만 말하지 않는다.** 타입은 도형, 우선순위는 화살표, 담당자는
 * 아바타 — 카드에 쓰는 조각을 그대로 얹어서 고르는 자리와 보이는 자리가
 * 같은 그림이 되게 한다.
 *
 * 색·간격·글자는 **토큰만** 쓴다. 리터럴 px 을 새로 만들지 마라 —
 * 이 조각이 화면 여럿에 퍼지므로 한 번 어긋나면 전부 어긋난다.
 */

export function ChoiceGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-s2">
      {children}
    </div>
  );
}

/**
 * 하나만 고르는 것(라디오). 같은 `name` 끼리 한 묶음이 되고, 그 묶음 안에서
 * 화살표로 옮겨 다닌다.
 *
 * `nullable` 이면 **고른 것을 다시 눌러 푼다.** 「안 정함」 칸을 따로 두지
 * 않으려는 것이다 — 넷 중 하나를 고르는 자리에 「없음」을 다섯째로 붙이면
 * 그것도 하나의 선택지처럼 보인다.
 */
/*
  ⚠ **라벨에 `relative` 를 지우지 마라**(2026-10-19 실측). 숨긴 `<input>` 은
  `sr-only`(= `position: absolute`)라 기준 상자가 없으면 **멀리 떨어진 조상
  기준으로 놓인다** — 휴대폰 바닥 시트에서 화면 밖(1052px)에 있었다. 누르면
  포커스가 그 자리로 가고, 브라우저가 그것을 보이려고 `overflow: hidden` 인
  시트 껍데기를 통째로 밀어 올려 **머리가 잘리고 바닥에 빈 띠가 생겼다.**
*/
export function Radio({
  name,
  checked,
  onChange,
  children,
  label,
  nullable = true,
}: {
  name: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  children: React.ReactNode;
  label: string;
  nullable?: boolean;
}) {
  return (
    <label className="relative cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={checked}
        aria-label={label}
        onChange={() => onChange(true)}
        onClick={() => {
          if (nullable && checked) onChange(false);
        }}
        className="peer sr-only"
      />
      <Face checked={checked}>{children}</Face>
    </label>
  );
}

/** 여럿을 고르는 것(체크박스). 라벨이 이것을 쓴다. */
export function Check({
  checked,
  onChange,
  children,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: React.ReactNode;
  label: string;
}) {
  return (
    <label className="relative cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <Face checked={checked}>{children}</Face>
    </label>
  );
}

/**
 * 겉모습. 라디오와 체크박스가 같은 얼굴을 쓴다 — 고르는 방식이 다를 뿐
 * 「값 하나를 켜고 끈다」는 같기 때문이다.
 *
 * **색만으로 켜짐을 말하지 않는다.** 테두리 굵기가 아니라 **테두리 색과
 * 바탕 둘**이 함께 바뀌고, 포커스에는 별도의 링이 선다.
 */
function Face({ checked, children }: { checked: boolean; children: React.ReactNode }) {
  return (
    <span
      className={[
        'inline-flex min-h-s7 items-center gap-s2 rounded-md border px-s3 text-meta transition-colors',
        'peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-panel',
        'max-[720px]:min-h-[44px]',
        checked
          ? 'border-brand bg-brand-t text-brand-d'
          : 'border-line bg-panel text-ink-2 hover:bg-sunk',
      ].join(' ')}
    >
      {children}
    </span>
  );
}
